import type { PropsWithChildren } from "react";
import { Navigate, useLocation } from "react-router";

import { AccessDeniedScreen } from "./AccessDeniedScreen";
import type { PlatformRole } from "./roles";
import { resolveRouteAccess } from "./route-access";
import { useSession } from "./session";
import { SessionPendingScreen } from "./SessionPendingScreen";

/**
 * Draws a route only when the signed-in role matches the audience the route
 * declares.
 *
 * ==========================================================================
 * THIS GATE IS NOT AUTHORIZATION. IT IS A DRAWING DECISION.
 * ==========================================================================
 *
 * It runs in the browser, on data the viewer controls. The API authorizes every
 * request again on the server (ADR 0023), and this gate exists only so people
 * are not shown screens they cannot use.
 *
 * Nobody signed in goes to sign-in, and comes back here afterwards.
 * `allowedRoles` is required, and an empty audience denies everyone.
 */
export type RouteAccessGateProps = PropsWithChildren<{
  allowedRoles: readonly PlatformRole[];
  /** Also required in the session's shop permissions (P5-013 W6). */
  permission?: string;
}>;

export function RouteAccessGate({ allowedRoles, children, permission }: RouteAccessGateProps) {
  const { session } = useSession();
  const location = useLocation();

  if (session.kind === "checking" || session.kind === "unavailable") {
    return <SessionPendingScreen />;
  }
  if (session.kind === "signed-out" || session.kind === "idle-locked") {
    return <Navigate replace state={{ from: location.pathname }} to="/sign-in" />;
  }

  const decision = resolveRouteAccess({ allowedRoles, permission, session });
  if (!decision.granted) {
    return <AccessDeniedScreen reason={decision.reason} />;
  }
  return <>{children}</>;
}
